export const INVOICE_STATUS_BADGES = {
  DRAFT: { bg: 'secondary', text: 'Borrador' },
  SENT: { bg: 'info', text: 'Enviada' },
  PAID: { bg: 'success', text: 'Pagada' },
  PARTIAL: { bg: 'warning', text: 'Parcialmente Pagada' },
  CANCEL_PENDING: { bg: 'warning', text: 'Cancelación en proceso' },
  CANCELLED: { bg: 'danger', text: 'Cancelada' },
}

export const isInvoiceCancelled = (status) => status === 'CANCELLED'

export const isInvoiceCancelPending = (status) => status === 'CANCEL_PENDING'

/** CFDI timbrado que no está cancelado efectivamente (incluye CANCEL_PENDING). */
export const isInvoiceTimbradaVigente = (invoice) =>
  Boolean(invoice?.cfdi_uuid) && !isInvoiceCancelled(invoice?.status)

export const canRequestCancelacion = (status) =>
  !isInvoiceCancelled(status) && !isInvoiceCancelPending(status)

export const getInvoiceStatusBadge = (status) =>
  INVOICE_STATUS_BADGES[status] || { bg: 'secondary', text: status || '—' }

/**
 * Prefijo del folio de un borrador de facturación recurrente. Debe coincidir con
 * `PREFIJO_FOLIO_PROVISIONAL` del backend (`services/core.py`).
 */
export const PREFIJO_FOLIO_PROVISIONAL = 'REC-'

/**
 * Si este folio es el marcador de un borrador recurrente y **no un consecutivo real**.
 *
 * Un borrador de recurrencia nace con `REC-<plantilla>-<periodo>` y **no gasta folio**: el oficial
 * se asigna al timbrar. Mostrarlo como un folio cualquiera le diría al contador que ya consumió un
 * número de su serie.
 */
export const esFolioProvisional = (invoiceNumber) =>
  String(invoiceNumber || '').startsWith(PREFIJO_FOLIO_PROVISIONAL)

/**
 * El periodo que lleva dentro un folio provisional: `REC-7-2026-10` → `2026-10`.
 * Devuelve cadena vacía si no es provisional.
 */
export const periodoDeFolioProvisional = (invoiceNumber) => {
  if (!esFolioProvisional(invoiceNumber)) return ''
  // REC-<plantilla>-<periodo>, y el periodo puede traer guiones: `2026-10` o `2026-10-16`.
  const resto = String(invoiceNumber).slice(PREFIJO_FOLIO_PROVISIONAL.length)
  const corte = resto.indexOf('-')
  return corte === -1 ? '' : resto.slice(corte + 1)
}

export const getDisplaySerieFolio = (invoiceNumber) => {
  // Solo el folio (último segmento). Para serie+folio usar buildCfdiFileBasename / configuracion_serie.
  if (!invoiceNumber) return 'N/A'

  // **Un folio provisional no se recorta.** Partir `REC-1-2026-10` por guiones devolvía `10`, que
  // es indistinguible del folio 10 de una serie: el contador veía un número y daba por hecho que
  // ya se había consumido. Aquí se muestra que está pendiente, con su periodo.
  if (esFolioProvisional(invoiceNumber)) {
    const periodo = periodoDeFolioProvisional(invoiceNumber)
    return periodo ? `Sin folio · ${periodo}` : 'Sin folio'
  }

  const parts = String(invoiceNumber).split('-')
  if (parts.length >= 2) return parts[parts.length - 1]
  return String(invoiceNumber)
}

export const extraerFolioNeto = (invoiceNumber) => {
  if (!invoiceNumber) return ''
  const parts = String(invoiceNumber).split('-')
  if (parts.length >= 2) return parts[parts.length - 1].trim()
  return String(invoiceNumber).trim()
}

/**
 * Mezclado serie+folio para UI/archivos.
 * Nunca incluye el cliente_id del identificador interno (PAGO-0007-000002 → PAGO-000002).
 */
export const buildSerieFolioDisplay = (serie, folio) => {
  const s = (serie || '').trim()
  const f = (folio || '').trim()
  if (s && f) return `${s}-${f}`
  return f || s || ''
}

/** Base de nombre de archivo CFDI (serie-folio o solo folio). Alineado con backend cfdi_filename_utils. */
export const buildCfdiFileBasename = (invoice) => {
  if (!invoice) return 'CFDI'
  if (invoice.cfdi_file_basename) return invoice.cfdi_file_basename

  const folio = extraerFolioNeto(invoice.invoice_number)
  const serie = (invoice.configuracion_serie || '').trim()
  return buildSerieFolioDisplay(serie, folio) || 'CFDI'
}

export const buildCfdiDownloadFilename = (invoice, extension) => {
  const ext = String(extension || '').replace(/^\./, '')
  return ext ? `${buildCfdiFileBasename(invoice)}.${ext}` : buildCfdiFileBasename(invoice)
}

export const getFilenameFromContentDisposition = (response, fallback = '') => {
  const cd = response?.headers?.['content-disposition'] || ''
  const quoted = /filename="([^"]+)"/i.exec(cd)
  if (quoted?.[1]) return quoted[1]
  const unquoted = /filename=([^;\s]+)/i.exec(cd)
  if (unquoted?.[1]) return unquoted[1].replace(/^"|"$/g, '')
  return fallback
}
